const executableMarkupPattern =
  /<\s*\/?\s*(script|iframe|object|embed|svg|style|link|meta|base|form|img|video|audio|input|button|details|a|body|html)\b|<[^>]*\bon[a-z]{3,}\s*=|(?:javascript|vbscript|data:text\/html):|&(?:lt|#x3c|#60);?\s*\/?\s*script\b|<\s*\/?[a-z][a-z0-9]*\b[^>]*>/i;

const DANGEROUS_HTML_PATTERNS = [
  /<\s*\/?\s*[a-zA-Z][^>]*>/i,                                   // Any complete HTML tag (<tag...>, </tag>)
  /<\s*\/?\s*(script|iframe|object|embed|svg|style|link|meta|base|form|img|video|audio|input|button|details|body|html|marquee|noscript|template|textarea|select|option|table|div|span|pre|code|blockquote|applet|frame|frameset|layer|bgsound|blink|xml)\b/i, // Incomplete dangerous tag without closing >
  /<\s*s\s*c\s*r\s*i\s*p\s*t/i,                                 // Obfuscated <script
  /\bon[a-z]{3,}[\s/]*=/i,                                       // Any event handler (onerror=, onload=, onclick=, onfocus=)
  /j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:/i,                 // Obfuscated javascript:
  /v\s*b\s*s\s*c\s*r\s*i\s*p\s*t\s*:/i,                         // Obfuscated vbscript:
  /d\s*a\s*t\s*a\s*:\s*t\s*e\s*x\s*t\s*\/\s*h\s*t\s*m\s*l/i,    // data:text/html
  /\bsrcdoc\s*=/i,                                               // iframe srcdoc
  /expression\s*\(/i,                                            // CSS expression(...)
];

const INVISIBLE_AND_CONTROL_CHARS_REGEX =
  /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\u200B-\u200D\uFEFF]/g;

const objectArtifactPattern = /\[object\s+[^\]]*\]/i;

// Literal text that only ever appears when a non-string value (object, array,
// NaN, undefined, a function, ...) was coerced to a string by mistake.
const RESERVED_STRINGIFIED_VALUES = new Set([
  "undefined",
  "null",
  "nan",
  "true",
  "false",
  "infinity",
  "-infinity",
]);
const jsonObjectLikePattern = /^\s*\{[\s\S]*\}\s*$/;
const jsonArrayLikePattern = /^\s*\[[\s\S]*\]\s*$/;
const stringifiedFunctionPattern = /^\s*(?:function\b|class\s|\(?[\w$,\s]*\)?\s*=>)/i;
const stringifiedRefPattern = /^\s*(?:Symbol\(|Promise\s*\{|\[native code\])/i;

// Narrow subset of RESERVED_STRINGIFIED_VALUES that is safe to reject on ANY
// payload field app-wide (unlike "true"/"false"/JSON-shaped strings, which
// can be legitimate values elsewhere, e.g. JSON.stringify'd FormData blobs).
const GENERIC_PLACEHOLDER_JUNK = new Set(["undefined", "null", "nan"]);

/**
 * Detects HTML tags, script blocks, event handlers, and pseudo-protocols.
 * Normalizes entity encodings to prevent bypasses, while safely permitting
 * legitimate medical notes and mathematical comparisons like "BP > 140" or "Age < 5".
 */
export const containsExecutableMarkup = (value) => {
  if (typeof value !== "string") return false;
  // Quick test against common patterns
  if (executableMarkupPattern.test(value)) return true;

  // Unescape common HTML entities like &lt; &gt; &#60; &#x3c; to catch encoded payloads
  let normalized = value.replace(/&lt;/gi, "<").replace(/&gt;/gi, ">");
  if (/&#x?[0-9a-f]+/i.test(normalized)) {
    normalized = normalized.replace(/&#x?([0-9a-f]+);?/gi, (match, hex) => {
      const code = match.toLowerCase().includes("x") ? parseInt(hex, 16) : parseInt(hex, 10);
      return !isNaN(code) && code > 0 && code < 65536 ? String.fromCharCode(code) : "";
    });
  }

  return DANGEROUS_HTML_PATTERNS.some((pattern) => pattern.test(normalized));
};

export const containsObjectArtifact = (value) =>
  typeof value === "string" && objectArtifactPattern.test(value);

/**
 * True when `value` is not a genuine, directly-typed string/number but the
 * result (or original form) of an object, array, function, boolean, or
 * non-value (NaN/undefined/null-as-text) that would otherwise be silently
 * coerced into a plausible-looking string (e.g. an array `["John"]` stringifies
 * to "John", `{}` becomes "[object Object]"). Checking the raw value's type
 * FIRST — before any `String(value)` call happens anywhere else — is what
 * actually stops these from slipping through as "valid" text.
 */
export const isStringifiedJunk = (value) => {
  if (value === null || value === undefined) return false; // absence is handled by required-field checks
  if (typeof value === "number") return !Number.isFinite(value);
  if (typeof value !== "string") return true; // objects, arrays, booleans, functions, symbols
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (RESERVED_STRINGIFIED_VALUES.has(lower)) return true;
  if (objectArtifactPattern.test(trimmed)) return true;
  if (jsonObjectLikePattern.test(trimmed) || jsonArrayLikePattern.test(trimmed))
    return true;
  if (
    stringifiedFunctionPattern.test(trimmed) ||
    stringifiedRefPattern.test(trimmed)
  )
    return true;
  return false;
};

/** Returns `value` unchanged only if it is already a genuine string, otherwise "". */
export const asSafeString = (value) =>
  typeof value === "string" ? value : "";

/**
 * Validates a person's name component (first name, last name, middle name).
 * Requirements:
 * - Must be a string of length 2 to 50
 * - Must contain only valid name characters (letters, spaces, hyphens, dots, apostrophes)
 * - Must have at least 2 alphabetic characters (rejects single letters like "t" or symbols)
 * - Must not contain executable markup or [object Object] artifacts
 */
export const isValidPersonName = (name) => {
  if (!name || typeof name !== "string") return false;
  const trimmed = name.trim();
  if (trimmed.length < 2 || trimmed.length > 50) return false;
  if (containsExecutableMarkup(trimmed) || containsObjectArtifact(trimmed))
    return false;

  // Check character set: Unicode letters, spaces, hyphens, periods, apostrophes
  const validCharsRegex = /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF' .\-]+$/;
  if (!validCharsRegex.test(trimmed)) return false;

  // Must contain at least 2 actual letters
  const letters = trimmed.match(/[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]/g);
  return letters !== null && letters.length >= 2;
};

/**
 * Validates a 10-digit mobile number.
 * Requirements:
 * - Must start with 6, 7, 8, or 9
 * - Must be exactly 10 digits
 * - Must not be an object string or contain letters
 */
export const isValidMobile = (mobile) => {
  if (!mobile) return false;
  if (typeof mobile === "object") return false;
  const cleaned = String(mobile).trim();
  if (containsObjectArtifact(cleaned)) return false;
  return /^[6-9]\d{9}$/.test(cleaned);
};

/**
 * Safe string conversion without artificial text mangling or regex stripping.
 */
export const cleanDisplayString = (value) => {
  if (value == null) return "";
  return typeof value === "string" ? value : String(value);
};

/**
 * Mobile string helper without artificial stripping.
 */
export const cleanMobile = (value) => {
  if (value == null) return "";
  return String(value);
};

/**
 * Sanitizes input values on keystroke or paste based on field semantics.
 * Physically prevents invalid characters (e.g. numbers in names, letters in phones, HTML tags)
 * from ever being entered into the form state.
 */
export const sanitizeInputByField = (field, rawValue) => {
  if (rawValue == null) return "";
  // Reject non-string/non-number values (and known stringified junk) before
  // any String() coercion can turn them into innocent-looking text.
  if (isStringifiedJunk(rawValue)) return "";
  const s = String(rawValue);

  switch (field) {
    case "f_name":
    case "firstName":
    case "m_name":
    case "middleName":
    case "l_name":
    case "lastName":
    case "fullName":
    case "name":
      // Only allow letters, spaces, hyphens, periods, apostrophes. Max 50 chars.
      return s
        .replace(/[^a-zA-Z\u00C0-\u024F\u1E00-\u1EFF' .\-]/g, "")
        .slice(0, 50);

    case "mobile":
    case "mobileNumber":
    case "phone":
    case "phoneNumber":
    case "emergencyContact":
      // Only allow digits 0-9. Max 10 digits.
      return s.replace(/\D/g, "").slice(0, 10);

    case "pincode":
    case "postalCode":
    case "zipCode":
      // Only allow digits 0-9. Max 6 digits.
      return s.replace(/\D/g, "").slice(0, 6);

    case "age":
      // Only allow digits. Max 3 digits.
      return s.replace(/\D/g, "").slice(0, 3);

    default:
      // Strip script fragments, executable markup, and object artifacts
      return sanitizeText(s, 500);
  }
};

/**
 * Strips script tags, style tags, HTML markup, and event handlers while
 * safely preserving legitimate clinical notes and mathematical comparisons (e.g. "BP > 140/90", "Age < 5").
 */
export const sanitizeText = (value, maxLength = 10000) => {
  if (value == null) return "";
  if (typeof value !== "string") {
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return "";
  }

  let str = value;

  // Unescape common HTML entities (&lt; &gt; &#60; &#62; &#x3c; &#x3e;) to catch entity-encoded payloads
  if (/&(?:lt|gt|#60|#62|#x3c|#x3e);?/i.test(str)) {
    str = str
      .replace(/&(?:lt|#60|#x3c);?/gi, "<")
      .replace(/&(?:gt|#62|#x3e);?/gi, ">");
  }

  // 1. Remove script, style, iframe, object, embed, svg blocks and contents
  str = str.replace(/<\s*script\b[^>]*>[\s\S]*?<\s*\/\s*script\s*>/gi, "");
  str = str.replace(/<\s*style\b[^>]*>[\s\S]*?<\s*\/\s*style\s*>/gi, "");
  str = str.replace(/<\s*iframe\b[^>]*>[\s\S]*?<\s*\/\s*iframe\s*>/gi, "");
  str = str.replace(/<\s*object\b[^>]*>[\s\S]*?<\s*\/\s*object\s*>/gi, "");
  str = str.replace(/<\s*embed\b[^>]*>[\s\S]*?<\s*\/\s*embed\s*>/gi, "");
  str = str.replace(/<\s*svg\b[^>]*>[\s\S]*?<\s*\/\s*svg\s*>/gi, "");

  // 2. Remove all HTML tags (closed and unclosed with tag names, e.g. <img src=x onerror=...>)
  str = str.replace(/<\s*\/?\s*[a-zA-Z][^>]*>/gi, "");
  str = str.replace(/<\s*[a-zA-Z][^>]*/gi, "");

  // 3. Remove dangerous URI schemes
  str = str.replace(/j\s*a\s*v\s*a\s*s\s*c\s*r\s*i\s*p\s*t\s*:/gi, "");
  str = str.replace(/v\s*b\s*s\s*c\s*r\s*i\s*p\s*t\s*:/gi, "");
  str = str.replace(/d\s*a\s*t\s*a\s*:\s*t\s*e\s*x\s*t\s*\/\s*h\s*t\s*m\s*l/gi, "");

  // 4. Remove event handlers with or without quotes
  str = str.replace(/\bon[a-z]{3,}[\s/]*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

  // 5. Remove object serialization artifacts like [object Object]
  str = str.replace(objectArtifactPattern, "");

  // 6. Strip invisible/control characters
  str = str.replace(INVISIBLE_AND_CONTROL_CHARS_REGEX, "");

  return str.trim().slice(0, maxLength);
};

export const sanitizeClinicalNote = (value, maxLength = 10000) => {
  return sanitizeText(value, maxLength);
};

export const sanitizeSearchQuery = (query) => {
  if (!query) return "";
  return sanitizeText(String(query), 200).replace(/[<>'"]/g, "");
};

/**
 * Sanitizes a URL for safe use in href/src attributes.
 * Blocks javascript:, vbscript:, data:text/html, and other dangerous protocols.
 * Only allows http:, https:, mailto:, tel:, and safe data: URIs (images/pdf).
 *
 * Production standard: OWASP recommends protocol allowlisting over blocklisting.
 * Used by GitHub, Slack, and Salesforce.
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== "string") return "#";
  const trimmed = url.replace(INVISIBLE_AND_CONTROL_CHARS_REGEX, "").trim();
  if (!trimmed) return "#";

  // Decode HTML entities to catch encoded protocol attacks
  let decoded = trimmed
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#x?([0-9a-f]+);?/gi, (match, hex) => {
      const code = match.toLowerCase().includes("x")
        ? parseInt(hex, 16)
        : parseInt(hex, 10);
      return !isNaN(code) && code > 0 && code < 65536
        ? String.fromCharCode(code)
        : "";
    });

  // Strip whitespace that could obfuscate the protocol (e.g. "j a v a s c r i p t:")
  const protocolCheck = decoded.replace(/\s+/g, "").toLowerCase();

  // Block dangerous protocols
  if (
    protocolCheck.startsWith("javascript:") ||
    protocolCheck.startsWith("vbscript:") ||
    protocolCheck.startsWith("data:text/html")
  ) {
    return "#";
  }

  // Allow safe protocols only
  const SAFE_PROTOCOLS = /^(https?:\/\/|mailto:|tel:|data:(image|application\/pdf|application\/octet-stream)[\s;,]|\/|#)/i;
  if (SAFE_PROTOCOLS.test(trimmed)) {
    return trimmed;
  }

  // Relative URLs (no protocol) are safe
  if (!trimmed.includes(":")) {
    return trimmed;
  }

  // Unknown protocol — block
  return "#";
};

/**
 * Recursively sanitizes any payload object, array, FormData, URLSearchParams,
 * or JSON-string, stripping XSS scripts from all strings while preserving
 * legitimate medical notes (e.g. "BP > 140/90", "Age < 5").
 *
 * Used as a silent safety-net when requests are dispatched or force-triggered.
 */
export const deepSanitizePayload = (obj) => {
  if (obj == null) return obj;

  if (typeof obj === "string") {
    const trimmed = obj.trim();
    // If it is a stringified JSON object or array, parse, deep-clean, and re-stringify
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        const cleaned = deepSanitizePayload(parsed);
        return JSON.stringify(cleaned);
      } catch {
        // Fall back to plain string inspection
      }
    }
    return containsExecutableMarkup(obj) ? sanitizeText(obj) : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepSanitizePayload(item));
  }

  if (typeof FormData !== "undefined" && obj instanceof FormData) {
    // Preserve FormData directly without reconstructing it so original File objects,
    // boundaries, and exact uploaded filenames are never altered or renamed.
    return obj;
  }

  if (typeof URLSearchParams !== "undefined" && obj instanceof URLSearchParams) {
    const cleanParams = new URLSearchParams();
    for (const [key, val] of obj.entries()) {
      cleanParams.append(key, deepSanitizePayload(val));
    }
    return cleanParams;
  }

  const isBlob = typeof Blob !== "undefined" && obj instanceof Blob;
  const isFile = typeof File !== "undefined" && obj instanceof File;
  if (typeof obj === "object" && !(obj instanceof Date) && !isBlob && !isFile) {
    const cleaned = {};
    for (const [k, v] of Object.entries(obj)) {
      cleaned[k] = deepSanitizePayload(v);
    }
    return cleaned;
  }

  return obj;
};

/**
 * Sanitizes every field of a plain data object (e.g. an ABHA profile response,
 * or a prefill/autofill source) before it is ever assigned into React state.
 * Any leaf that isn't already a genuine string/number is blanked instead of
 * being coerced with String(), which is what stops an object or array field
 * from a third-party API response turning into "[object Object]" or a
 * comma-joined string once it lands in a form/payload.
 */
export const sanitizeProfileFields = (data) => {
  if (!data || typeof data !== "object") return {};
  const sanitized = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value == null) {
      sanitized[key] = "";
      return;
    }
    if (typeof value !== "string" && typeof value !== "number") {
      sanitized[key] = ""; // reject objects/arrays/booleans/functions outright
      return;
    }
    sanitized[key] = sanitizeInputByField(key, value);
  });
  return sanitized;
};

/**
 * Validates a single patient field according to strict business and security rules.
 * Returns an error message string if invalid, or null if valid.
 */
export const validatePatientField = (field, value, isRequired = false) => {
  if (isStringifiedJunk(value)) {
    return 'Invalid value: this field cannot contain an object, array, code, or placeholder text (e.g. "undefined", "[object Object]", JSON).';
  }
  const str = value == null ? "" : String(value).trim();

  // Check required
  if (isRequired && !str) {
    switch (field) {
      case "f_name":
      case "firstName":
        return "First name is required.";
      case "l_name":
      case "lastName":
        return "Last name is required.";
      case "fullName":
      case "name":
        return "Patient name is required.";
      case "mobile":
      case "mobileNumber":
      case "phone":
        return "Mobile number is required.";
      case "dob":
      case "dateOfBirth":
        return "Date of birth is required.";
      case "gender":
        return "Gender is required.";
      case "address":
        return "Address is required.";
      default:
        return "This field is required.";
    }
  }

  if (!str) return null; // Optional and empty is valid

  // Check security violations on any field
  if (containsExecutableMarkup(str) || containsObjectArtifact(str)) {
    return "Contains invalid characters, script tags, or object text.";
  }

  switch (field) {
    case "f_name":
    case "firstName":
      if (str.length < 2) return "First name must be at least 2 characters.";
      if (!isValidPersonName(str))
        return "First name must contain only letters (at least 2).";
      return null;

    case "l_name":
    case "lastName":
      if (str.length < 2) return "Last name must be at least 2 characters.";
      if (!isValidPersonName(str))
        return "Last name must contain only letters (at least 2).";
      return null;

    case "m_name":
    case "middleName":
      if (!isValidPersonName(str))
        return "Middle name must contain only letters.";
      return null;

    case "fullName":
    case "name": {
      if (str.length < 2) return "Patient name must be at least 2 characters.";
      const parts = str.split(/\s+/).filter(Boolean);
      if (parts.length === 0 || !isValidPersonName(parts[0])) {
        return "First name must contain only letters (at least 2).";
      }
      if (parts.length > 1 && !isValidPersonName(parts[parts.length - 1])) {
        return "Last name must contain only letters (at least 2).";
      }
      return null;
    }

    case "mobile":
    case "mobileNumber":
    case "phone":
      if (str.length !== 10) return "Mobile number must be exactly 10 digits.";
      if (!/^[6-9]/.test(str))
        return "Mobile number must start with 6, 7, 8, or 9.";
      if (!isValidMobile(str))
        return "Please enter a valid 10-digit mobile number.";
      return null;

    case "dob":
    case "dateOfBirth": {
      const d = new Date(`${str}T00:00:00`);
      if (Number.isNaN(d.getTime())) return "Invalid date of birth.";
      const now = new Date();
      if (d > now) return "Date of birth cannot be in the future.";
      const year = d.getFullYear();
      if (year < 1900 || now.getFullYear() - year > 125) {
        return "Please enter a realistic date of birth.";
      }
      return null;
    }

    case "gender": {
      const g = str.toLowerCase();
      if (!["male", "female", "other", "m", "f", "o"].includes(g)) {
        return "Please select a valid gender (Male, Female, Other).";
      }
      return null;
    }

    case "email":
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) {
        return "Please enter a valid email address.";
      }
      return null;

    case "pincode":
    case "postalCode":
      if (!/^\d{6}$/.test(str)) return "Pincode must be exactly 6 digits.";
      return null;

    case "address":
      if (str.length < 3) return "Address must be at least 3 characters.";
      return null;

    default:
      return null;
  }
};

export const assertSafePayload = (value, path = "request") => {
  if (typeof value === "string") {
    if (containsExecutableMarkup(value)) {
      const error = new Error(
        "Input contains unsupported HTML or script code. Remove tags and enter plain text.",
      );
      error.name = "InputValidationError";
      error.code = "INPUT_VALIDATION_ERROR";
      throw error;
    }
    // Deliberately narrow: some legitimate payloads carry JSON.stringify'd
    // blobs (e.g. FormData fields in clinical assessments), so only reject
    // unambiguous stringification artifacts here, not anything JSON-shaped.
    if (
      containsObjectArtifact(value) ||
      GENERIC_PLACEHOLDER_JUNK.has(value.trim().toLowerCase())
    ) {
      const error = new Error(
        `Input at ${path} looks like stringified object/code data instead of plain text.`,
      );
      error.name = "InputValidationError";
      error.code = "INPUT_VALIDATION_ERROR";
      throw error;
    }
    return;
  }

  if (typeof value === "number" && !Number.isFinite(value)) {
    const error = new Error(`Input at ${path} must be a finite number.`);
    error.name = "InputValidationError";
    error.code = "INPUT_VALIDATION_ERROR";
    throw error;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertSafePayload(item, `${path}[${index}]`),
    );
    return;
  }

  if (typeof FormData !== "undefined" && value instanceof FormData) {
    for (const [key, nestedValue] of value.entries()) {
      assertSafePayload(nestedValue, `${path}.${key}`);
    }
    return;
  }

  if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) {
    for (const [key, nestedValue] of value.entries()) {
      assertSafePayload(nestedValue, `${path}.${key}`);
    }
    return;
  }

  const isBlob = typeof Blob !== "undefined" && value instanceof Blob;
  const isFile = typeof File !== "undefined" && value instanceof File;
  if (value && typeof value === "object" && !isBlob && !isFile) {
    // Deep patient validation if object has patient properties. This uses
    // the full isStringifiedJunk check (via validatePatientField) because a
    // JSON/object-shaped value is never valid for these specific fields.
    const patientFields = [
      "f_name",
      "firstName",
      "m_name",
      "middleName",
      "l_name",
      "lastName",
      "mobile",
      "mobileNumber",
      "fullName",
      "name",
      "address",
      "email",
      "pincode",
    ];
    for (const f of patientFields) {
      if (value[f] !== undefined) {
        const err = validatePatientField(f, value[f], false);
        if (err) {
          const error = new Error(`Validation failed for ${f}: ${err}`);
          error.name = "InputValidationError";
          error.code = "INPUT_VALIDATION_ERROR";
          throw error;
        }
      }
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      assertSafePayload(nestedValue, `${path}.${key}`);
    });
  }
};

export {
  validateDocumentFile,
  validateDocumentBatch,
  MAX_DOCUMENT_FILE_SIZE,
  ALLOWED_DOCUMENT_EXTENSIONS,
  ALLOWED_DOCUMENT_MIME_TYPES,
} from "./fileSecurityValidation.js";



